/**
 * If you are not familiar with React Navigation, refer to the "Fundamentals" guide:
 * https://reactnavigation.org/docs/getting-started
 *
 */
import React, { useEffect, useState } from "react";
import { FontAwesome } from '@expo/vector-icons';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { NavigationContainer, DefaultTheme, DarkTheme } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { ColorSchemeName, Pressable } from 'react-native';

import tw from "../styles/tailwind";
import useStaff from "../hooks/useStaff";
import useTicketPushNavigation from "../hooks/useTicketPushNavigation";



import { RootStackParamList, RootTabParamList, RootTabScreenProps } from '../types';
import LinkingConfiguration from './LinkingConfiguration';

// 
import { useSelector, useDispatch } from "react-redux";
import { updateStatus } from "../store/Reducers/status";


/**
 * Import de las pantallas
 */
import LoginScreen from '../screens/Auth/LoginScreen'
import InvitationScreen from "../screens/Invitation/InvitationScreen";
import PrivacyScreen from "../screens/ToS/PrivacyScreen";
import SupportScreen from "../screens/ToS/SupportScreen";
import EntryPassDetail from "../screens/Main/EntryPass/EntryPassDetail";


// Admin
import AdminStackNavigator from "./AdminStack";
import VinteStackNavigator from "./VinteStack";
import MetaAdminStackNavigator from "./MetaAdminStack";

// Vigilancia
import GuardStackNavigator from "./GuardStack";


// Vigilancia - Pantalla de dashboard de accesos
import AccessDashboardScreen from "@/screens/Community/Security/AccessDashboardScreen";



import NotFoundScreen from '../screens/NotFoundScreen';
import ImpersonationBanner from '../components/ImpersonationBanner';
import { View } from 'react-native';
import { navigationRef } from './navigationRef';


// Pantallas que vienen con Expo de TabNavigations
import ModalScreen from '../screens/Examples/TabNavigation/ModalScreen';
import TabOneScreen from '../screens/Examples/TabNavigation/TabOneScreen';
import TabTwoScreen from '../screens/Examples/TabNavigation/TabTwoScreen';


const MyTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    // primary: 'rgb(255, 45, 85)',
    // background: theme.BACKGROUND_COLOR,
    border: "transparent",
  },
};



export default function Navigation({ colorScheme }: { colorScheme: ColorSchemeName }) {
  return (
    <View style={{ flex: 1 }}>
      <ImpersonationBanner />
      <NavigationContainer
        ref={navigationRef}
        linking={LinkingConfiguration}
        //
        // si se quiere usar tema oscuro hay que descomentar esto
        //
        // theme={colorScheme === 'dark' ? DarkTheme : DefaultTheme}
        >
        {/*
          Este se puede intercambiar a el RootTabNavigator que
          trae la app demo de Expo
        */}
        <RootNavigator />
        {/* <RootTabNavigator /> */}
      </NavigationContainer>
    </View>
  );
}



/**
 * A root stack navigator is often used for displaying modals on top of all other content.
 * https://reactnavigation.org/docs/modal
 */
const Stack = createNativeStackNavigator<RootStackParamList>();



function RootNavigator() {
  const auth = useSelector((state) => state.auth);
  const status = useSelector((state) => state.status);
  const dispatch = useDispatch()

  // Vista de vigilancia si la consola es lo único que tiene; si no, entra a ella
  // desde Administración (Guard.Stack).
  const { guardOnly } = useStaff();

  // Despachamos la accion para actualizar el status de la app
  // Tocar un push de ticket abre su detalle.
  useTicketPushNavigation();

  useEffect(() => {
    dispatch(updateStatus())
  }, [auth.isLogged]);

  return (
    <Stack.Navigator
      screenOptions={{
        // Ejemplo de customizacion del header
        headerStyle: {
          backgroundColor: tw.color('primary'),
        },
        headerTintColor: tw.color('secondary'),
        headerTitleStyle: {
          fontWeight: 'bold',
        },
        // headerShown: false, // si se quiere escondido
      }}
    >
      {auth.isLogged ? (
        <>
          <Stack.Screen
            name="Main.Stack"
            component={guardOnly ? GuardStackNavigator : MainStackNavigator}
            options={{ headerShown: false }}
          />

          {/* Consola de vigilancia de una comunidad, abierta desde Administración */}
          <Stack.Screen
            name="Guard.Stack"
            component={GuardStackNavigator}
            options={{ headerShown: false }}
          />



          {/* 
          
          */}
          <Stack.Screen
            name="Admin.Dashboard"
            component={AdminStackNavigator}
            options={{ headerShown: false }}

          />
         

          {/*

          */}
          <Stack.Screen
            name="Vinte.Home"
            component={VinteStackNavigator}
            options={{ headerShown: false }}

          />

          <Stack.Screen
            name="MetaAdmin.Stack"
            component={MetaAdminStackNavigator}
            options={{ headerShown: false }}
          />
        </>
      ) : (
        <>
          {/* El stack de Auth */}
          <Stack.Screen
            name="Auth.Login"
            component={LoginScreen}
            options={{ title: "Iniciar sesión", headerShown: false }}
          />
        </>
      )}


      <Stack.Screen
        name="Invitation.Index"
        component={InvitationScreen}
        options={{ title: "Bienvenido", headerShown: false }}
      />

      <Stack.Screen
        name="EntryPass.Detail"
        component={EntryPassDetail}
        options={{ title: "Pase de entrada", headerShown: false }}
      />

      <Stack.Screen
        name="ToS.Privacy"
        component={PrivacyScreen}
        options={{ title: "Privacidad", headerShown: true }}
      />

      <Stack.Screen
        name="Support.Index"
        component={SupportScreen}
        options={{ title: "Soporte", headerShown: true }}
      />

      <Stack.Screen
        name="Dashboards.EntryLogs"
        component={AccessDashboardScreen}
        options={{ title: "Entradas y salidas", headerShown: false }}
      />

    </Stack.Navigator>
  )
}





/**
 * A bottom tab navigator displays tab buttons on the bottom of the display to switch screens.
 * https://reactnavigation.org/docs/bottom-tab-navigator
 */
import Colors from '../screens/Examples/TabNavigation/constants/Colors';
import useColorScheme from '../hooks/useColorScheme';
import MainStackNavigator from "./MainStack";

const BottomTab = createBottomTabNavigator<RootTabParamList>();

function RootTabNavigator() {
  return (
    <Stack.Navigator>
      <Stack.Screen name="Root" component={BottomTabNavigator} options={{ headerShown: false }} />
      <Stack.Screen name="NotFound" component={NotFoundScreen} options={{ title: 'Oops!' }} />
      <Stack.Group screenOptions={{ presentation: 'modal' }}>
        <Stack.Screen name="Modal" component={ModalScreen} />
      </Stack.Group>
    </Stack.Navigator>
  );
}


function BottomTabNavigator() {
  const colorScheme = useColorScheme();

  return (
    <BottomTab.Navigator
      initialRouteName="TabOne"
      screenOptions={{
        tabBarActiveTintColor: Colors[colorScheme].tint,
      }}>
      <BottomTab.Screen
        name="TabOne"
        component={TabOneScreen}
        options={({ navigation }: RootTabScreenProps<'TabOne'>) => ({
          title: 'Tab One',
          tabBarIcon: ({ color }) => <TabBarIcon name="code" color={color} />,
          headerRight: () => (
            <Pressable
              onPress={() => navigation.navigate('Modal')}
              style={({ pressed }) => ({
                opacity: pressed ? 0.5 : 1,
              })}>
              <FontAwesome
                name="info-circle"
                size={25}
                color={Colors[colorScheme].text}
                style={{ marginRight: 15 }}
              />
            </Pressable>
          ),
        })}
      />
      <BottomTab.Screen
        name="TabTwo"
        component={TabTwoScreen}
        options={{
          title: 'Tab Two',
          tabBarIcon: ({ color }) => <TabBarIcon name="code" color={color} />,
        }}
      />
    </BottomTab.Navigator>
  );
}

/**
 * You can explore the built-in icon families and icons on the web at https://icons.expo.fyi/
 */
function TabBarIcon(props: {
  name: React.ComponentProps<typeof FontAwesome>['name'];
  color: string;
}) {
  return <FontAwesome size={30} style={{ marginBottom: -3 }} {...props} />;
}
